// Application shell: canvas sizing (DPR cap 2), the fixed-timestep 60Hz logic
// loop with a 30fps render cap on coarse pointers, scene management, and the
// shared services every scene uses. Scenes draw in CSS-pixel space (the context
// is pre-scaled by DPR).

import { Input } from "./input";
import { GameAudio } from "./audio";
import { Assets } from "./assets";
import { Storage } from "./storage";
import { RNG, randomSeed } from "./rng";
import type { Difficulty } from "../game/config";

export interface Scene {
  enter?(): void;
  update(dt: number): void;
  render(): void;
  resize?(): void;
  dispose?(): void;
}

/** Data handed between scenes for a match and its results. */
export interface Flow {
  mode: "ai" | "online";
  difficulty: Difficulty;
  playerFighterId: string;
  oppFighterId: string;
  netMode: "ranked" | "room";
  room: string;
  /** True when this client CREATED the private room (server rate-limits creates). */
  roomCreate: boolean;
  seed: number;
  result?: {
    won: boolean;
    rounds: number;
    roundsWon: number;
    roundsLost: number;
    dmgDealt: number;
    dmgTaken: number;
    crowStrikes: number;
    oppName: string;
    eloLine?: string;
  };
}

const STEP = 1 / 60;

export class App {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  w = 0;
  h = 0;
  dpr = 1;
  coarse = false;
  input: Input;
  audio = new GameAudio();
  assets = new Assets();
  storage = new Storage();
  rng = new RNG(randomSeed());
  flow: Flow;

  private scene: Scene | null = null;
  private acc = 0;
  private last = 0;
  private renderAcc = 0;
  private raf = 0;

  constructor(mount: HTMLElement) {
    this.canvas = document.createElement("canvas");
    this.canvas.style.display = "block";
    this.canvas.style.touchAction = "none";
    this.canvas.style.width = "100%";
    this.canvas.style.height = "100%";
    mount.innerHTML = "";
    mount.appendChild(this.canvas);
    const c = this.canvas.getContext("2d", { alpha: false });
    if (!c) throw new Error("Canvas 2D unavailable");
    this.ctx = c;
    this.input = new Input(this.canvas);
    this.coarse = window.matchMedia?.("(pointer: coarse)").matches ?? false;
    this.flow = {
      mode: "ai",
      difficulty: "normal",
      playerFighterId: "spurblade",
      oppFighterId: "emberplume",
      netMode: "ranked",
      room: "",
      roomCreate: false,
      seed: randomSeed(),
    };
    // Mute preference persists across sessions (storage.ts SaveData.muted).
    this.audio.muted = this.storage.data.muted;
    this.audio.onMuteChange = (muted) => {
      this.storage.data.muted = muted;
      this.storage.save();
    };
    window.addEventListener("resize", this.onResize);
    window.addEventListener("orientationchange", this.onResize);
    this.resize();
  }

  private onResize = (): void => this.resize();

  resize(): void {
    // DPR cap 2 on desktop; 1.6 on coarse pointers — phones ship DPR 2.5–3.5
    // and rastering the full arena at cap-2 was pure fill-rate cost (measured
    // long frames under 4x CPU throttle). At 1.6 the art still reads crisp on
    // a ~6" panel and every full-canvas pass touches ~36% fewer pixels.
    this.dpr = Math.min(this.coarse ? 1.6 : 2, window.devicePixelRatio || 1);
    const rect = this.canvas.getBoundingClientRect();
    this.w = Math.max(1, Math.floor(rect.width));
    this.h = Math.max(1, Math.floor(rect.height));
    this.canvas.width = Math.floor(this.w * this.dpr);
    this.canvas.height = Math.floor(this.h * this.dpr);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.scene?.resize?.();
  }

  setScene(scene: Scene): void {
    this.scene?.dispose?.();
    // Swallow clicks queued across the switch and rapid double-tap follow-ups:
    // the second tap of a double-tap must not land on whatever button occupies
    // the same spot in the incoming scene (e.g. TAP TO START → LEADERBOARD).
    this.input.suppressFor(250);
    this.scene = scene;
    scene.resize?.();
    scene.enter?.();
  }

  start(): void {
    this.last = performance.now();
    const frame = (now: number): void => {
      this.raf = requestAnimationFrame(frame);
      let dt = (now - this.last) / 1000;
      this.last = now;
      if (dt > 0.1) dt = 0.1; // clamp after tab-away / GC pause
      this.input.beginFrame();

      let ran = false;
      this.acc += dt;
      while (this.acc >= STEP) {
        this.scene?.update(STEP);
        this.acc -= STEP;
        ran = true;
      }
      this.input.endFrame(ran);

      // Render throttle: 30fps on coarse pointers, 60fps otherwise. Subtract
      // the interval instead of zeroing so leftover time carries over —
      // zeroing dropped ~1 frame in 30 (57–58fps effective) on desktop.
      this.renderAcc += dt;
      const interval = this.coarse ? 1 / 30 : 1 / 61;
      if (this.renderAcc >= interval) {
        this.renderAcc = Math.min(this.renderAcc - interval, interval);
        this.scene?.render();
      }
    };
    this.raf = requestAnimationFrame(frame);
  }

  stop(): void {
    cancelAnimationFrame(this.raf);
  }

  /** Clears the whole canvas to the base background each render. */
  clear(color: string): void {
    this.ctx.fillStyle = color;
    this.ctx.fillRect(0, 0, this.w, this.h);
  }
}
